import { UIIcon } from '../config/icons';
import { ABEventSelect } from '../lib/ab/select-admin';
import { EDNewBlock } from '../lib/ed/block';
import { EDObject, EDEditor } from '../lib/ed/editor';
import { ed_email_hr_block } from '../lib/ed/email/basic';
import { ed_email_button_block } from '../lib/ed/email/button';
import { ed_email_decoration_values, EDEmailEditor } from '../lib/ed/email/editor';
import { ed_email_map_block } from '../lib/ed/email/map';
import { EmailSection } from '../lib/ed/email/section';
import { ed_email_calendar_block, ed_email_instructions_block, ed_email_session_list_block } from '../lib/ed/email/session';
import { ed_email_text_block } from '../lib/ed/email/text';
import { EDFields } from '../lib/ed/fields';
import { EDImageInput } from '../lib/ed/image';
import { EDI18NInput, EDI18NSpan } from '../lib/ed/input';
import { cEventAdminViewGet } from '../lib/mdl/c-event-admin';
import { MEmailSection, MEmailBlock, MLink, MContext, MEmailFrom, MEmailAttachment, mTemplateFieldsForContext } from '../lib/mdl/m-template';
import { UIValueGroup } from '../lib/ui/component/groupdropdown';
import { UILabeled } from '../lib/ui/component/labeled';
import { UISelect } from '../lib/ui/component/select';
import { UICheckbox, UIMultiToggles } from '../lib/ui/component/toggle';
import { $el } from '../lib/ui/elements';
import { UIValue, UIClassesSpec } from '../lib/ui/types';
import { UIView, UIViewSpec } from '../lib/ui/view';
import { $cdn } from '../lib/y/cdn';
import { $log, xavis_experiments } from '../lib/y/debug';
import { $on } from '../lib/y/events';
import { YForm, $form } from '../lib/y/form';
import { YLanguage } from '../lib/y/i18n';

class EmailHeader extends UIView implements EDObject {
    form: YForm;
    name = 'Header';
    parent: AdminEmailEditor;
    properties: UIViewSpec;

    constructor(parent: AdminEmailEditor) {
        super('div', 'ed-object tl-email-header c-light');
        this.ui_elem.ed_object = this;

        const button = new UICheckbox({ title: 'With button' });
        const button_text = new EDI18NInput({ multiline: false, placeholder: 'Button', classes: 'r-button p-f-text' });
        const button_link = new UISelect(parent.link_values);

        const image_values: UIValue<string>[] = [{ key: '', name: 'None' }, { key: 'event', name: 'Custom event' }, { key: 'custom', name: 'Custom image' }];
        if(parent.has_event)
            image_values.push({ key: 'data', name: 'Event image' });
        const image = new UISelect(image_values);
        const image_event = new ABEventSelect({ placeholder: 'Event' });
        const image_custom = new EDImageInput({ scope: 'template', fixed_width: true });

        this.form = $form({ image, image_event, image_custom, button, button_text, button_link });

        this.parent = parent;
        this.properties = $el.DIV('l-form', [
            button,
            new UILabeled(button_link, { above: 'Button link' }),
            new UILabeled(image, { above: 'Background image' }),
            image_event,
            image_custom
        ]);

        image_event.ui_hide();
        image_custom.ui_hide();
        button_text.ui_hide();
        button_link.ui_hide();

        this.root.addEventListener('click', e => {
            parent.selectObject(this);
            e.stopPropagation();
        });

        this.ui_add([
            $el.DIV('r-logo'),
            button_text
        ]);

        $on(button, 'value', v => {
            button_text.ui_show(undefined, !!v);
            button_link.ui_show(undefined, !!v);
        });

        const setHeaderImage = (v?: string) => {
            $log('app', 'setHeaderImage', v, image_custom.value, image_event.value);
            if(v == 'custom') {
                const image = image_custom.value?.image;
                if(image) {
                    this.root.style.setProperty('--tl-email-header-image', `url(${$cdn.media(image)})`);
                    return
                }
            } else if(v == 'event') {
                const event = image_event.value;
                if(event) {
                    cEventAdminViewGet(event).then(ev => {
                        if(ev.picture_ids?.length)
                            this.root.style.setProperty('--tl-email-header-image', `url(${$cdn.media(ev.picture_ids[0])})`);
                    });
                    return;
                }
            }

            this.root.style.setProperty('--tl-email-header-image', null);
        };

        $on(image, 'value', v => {
            this.classList.toggle('m-with-bg', !!v);
            this.classList.toggle('c-light', !v);
            this.classList.toggle('c-dark', !!v);
            setHeaderImage(v);
        });

        $on(image_event, 'value', () => {
            if(image.value == 'event')
                setHeaderImage('event');
        });

        $on(image_custom, 'change', () => {
            if(image.value == 'custom')
                setHeaderImage('custom');
        });

        $on(image, 'value', v => {
            image_event.ui_show(undefined, v == 'event');
            image_custom.ui_show(undefined, v == 'custom');
        });
    }
}

class EmailFooter extends UIView implements EDObject {
    form: YForm;
    name = 'Footer';
    parent: EDEditor<MEmailSection, MEmailBlock, EDEmailEditor>;
    properties: UIViewSpec;

    constructor(parent: EDEditor<MEmailSection, MEmailBlock, EDEmailEditor>) {
        super('div', 'ed-object tl-email-footer');
        this.ui_elem.ed_object = this;

        const unsubscribe = new UICheckbox({ title: 'Unsubscribe link' });

        this.form = $form({ unsubscribe });

        this.parent = parent;
        this.properties = $el.DIV('l-form', [
            unsubscribe
        ]);

        this.root.addEventListener('click', e => {
            parent.selectObject(this);
            e.stopPropagation();
        });

        const unsubscribe_link = $el.DIV('r-button', new EDI18NSpan({ en: 'Unsubscribe from emails from Abundo', sv: 'Avregistrera dig från e-postmeddelanden från Abundo', de: 'Abmelden von E-Mails von Abundo' }));
        unsubscribe_link.ui_hide();

        this.ui_add([
            $el.DIV('r-social', [
                $el.DIV('r-facebook'),
                $el.DIV('r-twitter'),
                $el.DIV('r-instagram')
            ]),
            $el.DIV('r-hashtag', '#abundo'),
            $el.DIV('r-button', new EDI18NSpan({ en: 'Help & Support', sv: 'Hjälp & Support', de: 'Hilfe & Support' })),
            unsubscribe_link,
            $el.DIV('r-button', new EDI18NSpan({ en: 'Add us as a secure sender', sv: 'Lägg till oss som säker avsändare', de: 'Füge uns als sicheren Absender hinzu' })),
            $el.DIV('r-bottom', [
                $el.B('© 2022 Abundo'),
                $el.B('support@abundolive.se'),
                $el.B('Grev Turegatan 30, 114 38 Stockholm'),
                $el.B('0222-281 10')
            ])
        ]);

        $on(unsubscribe, 'value', v => {
            unsubscribe_link.ui_show(undefined, !!v);
        });

    }
}

export class AdminEmailEditor extends EDEditor<MEmailSection, MEmailBlock, EDEmailEditor> {
    form: YForm;
    link_values: UIValue<MLink>[];
    has_event: boolean;

    constructor(context: MContext, name: string, languages: YLanguage[], classes?: UIClassesSpec) {
        const from_values: UIValue<MEmailFrom>[] = [{ key: 'noreply', name: 'No reply (noreply@abundolive.se)' }, { key: 'support', name: 'Support (support@abundolive.se)' }, { key: 'info', name: 'Info (info@abundolive.se)' }, { key: 'system', name: 'System (system@abundolive.se)' }, { key: 'producer_personal', name: 'Producer personal (oskar.linse@abundolive.se / dominique.keufen@abundolive.de)' }]
        const [fields, link_values] = mTemplateFieldsForContext(context);
        const attachment_values: UIValue<MEmailAttachment>[] = [];

        const new_blocks: UIValueGroup<EDNewBlock<MEmailBlock>>[] = [{
            name: 'Content',
            values: [
                { name: 'Text', icon: UIIcon.TEXT, block: { type: 'text', text: {}, columns: false } },
                { name: 'Button', icon: UIIcon.RECTANGLE_WIDE, block: { type: 'button', text: {}, link_type: 'url' } },
                { name: 'Separator', icon: UIIcon.HORIZONTAL_RULE, block: { type: 'hr' } },
            ]
        }, {
            name: 'Blocks',
            values: [
                { name: 'Map', icon: UIIcon.MAP_LOCATION, block: { type: 'map' } },
                { name: 'Calendar', icon: UIIcon.CALENDAR, block: { type: 'calendar' } },
                { name: 'Instructions', icon: UIIcon.ALIGN_LEFT, block: { type: 'instructions' } },
                { name: 'Session list', icon: UIIcon.LIST_UL, block: { type: 'session_list' } },
            ]
        }];

        const block_specs = [
            ed_email_text_block,
            ed_email_button_block,
            ed_email_hr_block
        ];

        const has_event = context == 'booking' || context == 'reservation' || context == 'purchase' || context == 'producer_event';

        if(context == 'booking' || context == 'reservation') {
            block_specs.push(ed_email_calendar_block, ed_email_map_block, ed_email_instructions_block);
            attachment_values.push(
                { key: 'ical', name: 'Calendar file' },
                { key: 'tickets', name: 'Tickets' }
            );
        }

        if(context == 'giftcard') {
            attachment_values.push(
                { key: 'giftcard', name: 'Gift card' },
                { key: 'receipt', name: 'Receipt' }
            );
        }

        if(context == 'producer_event' || context == 'booking_list') {
            block_specs.push(ed_email_session_list_block);
        }

        if(context == 'booking_list') {
            attachment_values.push(
                { key: 'booking_list', name: 'Booking list' }
            );
        }

        super(new EDFields(fields), block_specs, new_blocks, name, languages, [classes, 'tl-email-editor c-dark']);

        this.link_values = link_values;
        this.has_event = has_event;

        const from = new UISelect(from_values);
        const subject = new EDI18NInput({ fields: this.fields, classes: 'p-f-text' });
        const decoration = new UISelect(ed_email_decoration_values);
        const attachments = new UIMultiToggles(attachment_values, v => new UICheckbox({ key: v.key, title: v.name }), { classes: 'l-form' });
        const schema = new UICheckbox({ title: 'Add event schema' })
        const header = new EmailHeader(this);
        const footer = new EmailFooter(this);

        this.form = $form({ from, subject, header: header.form, footer: footer.form, sections: this.sections, decoration, schema, attachments });

        this.properties = $el.DIV('l-form', [
            new UILabeled(decoration, { above: 'Decoration' }),
            // has_event && schema
            xavis_experiments && !!attachment_values.length && new UILabeled(attachments, { above: 'Attachments' }),
        ]);

        this.pre_objects = [header];
        this.post_objects = [footer];
        this.content.ui_prepend([
            $el.DIV('tl-email-subject', [
                $el.DIV('r-from', [$el.I('From'), from]),
                $el.DIV('r-label', 'Subject'),
                subject
            ]),
            $el.DIV('tl-email-decoration', [$el.DIV('r-top'), $el.DIV('r-left'), $el.DIV('r-right')]),
            header
        ]);

        this.content.ui_add([
            footer
        ]);

        this.content.addEventListener('click', e => {
            this.selectObject(this);
            e.stopPropagation();
        });

        $on(decoration, 'value', v => {
            footer.classList.toggle('m-decoration', v == 'pink');
            this.classList.toggle('m-decoration', v == 'pink');
            this.classList.toggle('c-dark', v != 'pink');
            this.classList.toggle('c-light', v == 'pink');
        });
    }

    protected override makeSection() {
        return new EmailSection(this);
    }

    protected override newSection(): MEmailSection {
        return { background: '#ffffff', blocks: [] };
    }
}